import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent, PointerEvent, ReactNode, RefObject, SyntheticEvent } from 'react'
import { createPortal } from 'react-dom'
import { isTopModal, lockScroll, pushModal, watchKeyboardInset } from './modal-lock'
import type { ModalEntry } from './modal-lock'
import { attachSheetDrag, clearDragStyles } from './modal-sheet'
import { PortalRoot } from './portal-root'
import styles from './modal.module.css'

export { closeTopModal, useOpenModals } from './modal-lock'

export type ModalProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  role?: 'dialog' | 'alertdialog'
  dismissible?: boolean
  initialFocus?: RefObject<HTMLElement | null>
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void
  closeLabel?: string
  bodyClassName?: string
}

type Phase = 'closed' | 'opening' | 'open' | 'closing'
type Snapshot = { title: ReactNode; description: ReactNode; children: ReactNode; footer: ReactNode }

const SHEET_QUERY = '(max-width: 560px)'
const OUT_MS = 320
const FOCUSABLE = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [role="combobox"]:not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"])'

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)

const restoreFocus = (ref: RefObject<HTMLElement | null>) => {
  const el = ref.current
  ref.current = null
  if (el?.isConnected) el.focus({ preventScroll: true })
}

const isSheet = () => typeof window !== 'undefined' && window.matchMedia(SHEET_QUERY).matches
const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  role = 'dialog',
  dismissible = true,
  initialFocus,
  onSubmit,
  closeLabel = 'Закрыть',
  bodyClassName,
}: ModalProps) {
  const [phase, setPhase] = useState<Phase>('closed')
  const [wasOpen, setWasOpen] = useState(false)
  const [mode, setMode] = useState<'center' | 'sheet'>('center')
  const [snap, setSnap] = useState<Snapshot | null>(null)
  const [host, setHost] = useState<HTMLDialogElement | null>(null)
  const lastRef = useRef<Snapshot>({ title, description, children, footer })

  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setPhase('opening')
      setMode(isSheet() ? 'sheet' : 'center')
      setSnap(null)
    } else if (phase !== 'closed') {
      setPhase('closing')
      // eslint-disable-next-line react-hooks/refs -- снимок последнего показанного рендера, не текущих пропсов
      setSnap(lastRef.current)
    }
  }

  const dialogRef = useRef<HTMLDialogElement | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const scrimRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const phaseRef = useRef<Phase>('closed')
  const dismissibleRef = useRef(dismissible)
  const onCloseRef = useRef(onClose)
  const restoreRef = useRef<HTMLElement | null>(null)
  const downOnScrim = useRef(false)
  const entryRef = useRef<ModalEntry | null>(null)
  const titleId = useId()
  const descId = useId()

  useEffect(() => {
    phaseRef.current = phase
    dismissibleRef.current = dismissible
    onCloseRef.current = onClose
    if (open) lastRef.current = { title, description, children, footer }
  })

  const setDialog = useCallback((el: HTMLDialogElement | null) => {
    dialogRef.current = el
    setHost(el)
  }, [])

  const nudge = useCallback(() => {
    const panel = panelRef.current
    if (!panel || reducedMotion() || typeof panel.animate !== 'function') return
    const frames =
      dialogRef.current?.dataset.mode === 'sheet'
        ? [{ transform: 'none' }, { transform: 'translateY(-10px)' }, { transform: 'none' }]
        : [{ transform: 'none' }, { transform: 'scale(1.018)' }, { transform: 'none' }]
    panel.animate(frames, { duration: 280, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' })
  }, [])

  const requestClose = useCallback(() => {
    if (phaseRef.current !== 'open') return
    if (!dismissibleRef.current) return nudge()
    phaseRef.current = 'closing'
    onCloseRef.current()
  }, [nudge])

  const active = phase !== 'closed'

  useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || phase !== 'opening') return
    dialog.dataset.mode = mode
    if (!dialog.open) {
      const focused = document.activeElement
      restoreRef.current = focused instanceof HTMLElement ? focused : null
      dialog.showModal()
    }
    dialog.scrollTop = 0
    const panel = panelRef.current
    const target = initialFocus?.current ?? (mode === 'sheet' ? panel : (panel?.querySelector<HTMLElement>(`:is(.${styles.body}, .${styles.foot}) :is(${FOCUSABLE})`) ?? panel))
    target?.focus({ preventScroll: true })
    panel?.getBoundingClientRect()
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setPhase('open'))
    })
    return () => cancelAnimationFrame(frame)
  }, [phase, mode, initialFocus])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || !active) return
    const entry: ModalEntry = { close: requestClose }
    entryRef.current = entry
    const unlock = lockScroll()
    const pop = pushModal(entry)
    const unwatch = dialog.dataset.mode === 'sheet' ? watchKeyboardInset(dialog) : () => undefined
    return () => {
      unwatch()
      pop()
      unlock()
      entryRef.current = null
    }
  }, [active, requestClose])

  useEffect(() => {
    const dialog = dialogRef.current
    const panel = panelRef.current
    const scrim = scrimRef.current
    if (!dialog || !panel || !scrim || !active || dialog.dataset.mode !== 'sheet') return
    return attachSheetDrag({
      panel,
      scrim,
      isHandle: (target) => target instanceof Element && target.closest(`.${styles.grab}, .${styles.head}`) !== null,
      canDrag: () => phaseRef.current === 'open',
      onDismiss: () => {
        if (dismissibleRef.current) requestClose()
        else {
          clearDragStyles(panel, scrim)
          nudge()
        }
      },
    })
  }, [active, requestClose, nudge])

  useEffect(() => {
    if (phase !== 'closing') return
    clearDragStyles(panelRef.current, scrimRef.current)
    const timer = window.setTimeout(() => {
      setPhase('closed')
      setSnap(null)
    }, OUT_MS)
    return () => window.clearTimeout(timer)
  }, [phase])

  useEffect(() => {
    if (phase !== 'closed') return
    const dialog = dialogRef.current
    if (dialog?.open) dialog.close()
    restoreFocus(restoreRef)
  }, [phase])

  useEffect(() => {
    const ref = dialogRef
    const restore = restoreRef
    return () => {
      if (ref.current?.open) ref.current.close()
      restoreFocus(restore)
    }
  }, [])

  useEffect(() => {
    const body = bodyRef.current
    const panel = panelRef.current
    if (!body || !panel || !active) return
    const edges = () => {
      panel.dataset.top = String(body.scrollTop <= 1)
      panel.dataset.end = String(body.scrollTop + body.clientHeight >= body.scrollHeight - 1)
    }
    edges()
    body.addEventListener('scroll', edges, { passive: true })
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(edges) : null
    observer?.observe(body)
    return () => {
      body.removeEventListener('scroll', edges)
      observer?.disconnect()
    }
  }, [active, phase])

  const onKeyDown = (e: KeyboardEvent<HTMLDialogElement>) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return
    e.preventDefault()
    e.stopPropagation()
    requestClose()
  }

  const onCancel = (e: SyntheticEvent<HTMLDialogElement>) => {
    if (e.nativeEvent.cancelable) e.preventDefault()
    requestClose()
  }

  const onNativeClose = () => {
    const dialog = dialogRef.current
    if (phaseRef.current !== 'open' || dialog?.open) return
    const entry = entryRef.current
    if (entry && !isTopModal(entry)) return
    phaseRef.current = 'closing'
    onCloseRef.current()
  }

  const onPointerDown = (e: PointerEvent<HTMLDialogElement>) => {
    downOnScrim.current = e.target === scrimRef.current
  }

  const onScrimClick = () => {
    const hit = downOnScrim.current
    downOnScrim.current = false
    if (hit) requestClose()
  }

  if (!active) return null

  const shown = phase === 'closing' && snap ? snap : { title, description, children, footer }
  const hasBody = shown.children !== undefined && shown.children !== null && shown.children !== false
  const inner = (
    <>
      {hasBody && (
        <div ref={bodyRef} className={bodyClassName ? `${styles.body} ${bodyClassName}` : styles.body}>
          {shown.children}
        </div>
      )}
      {shown.footer && <footer className={styles.foot}>{shown.footer}</footer>}
    </>
  )

  return createPortal(
    <dialog
      ref={setDialog}
      className={styles.dialog}
      role={role === 'alertdialog' ? 'alertdialog' : undefined}
      aria-labelledby={titleId}
      aria-describedby={shown.description ? descId : undefined}
      data-state={phase}
      data-size={size}
      onKeyDown={onKeyDown}
      onCancel={onCancel}
      onClose={onNativeClose}
      onPointerDown={onPointerDown}
    >
      <PortalRoot value={host}>
        <div ref={scrimRef} className={styles.scrim} onClick={onScrimClick} />
        <div ref={panelRef} className={styles.panel} tabIndex={-1}>
          <div className={styles.grab} aria-hidden="true" />
          <header className={styles.head}>
            <h2 id={titleId} className={styles.title}>
              {shown.title}
            </h2>
            {shown.description && (
              <p id={descId} className={styles.description}>
                {shown.description}
              </p>
            )}
          </header>
          {onSubmit ? (
            <form className={styles.form} onSubmit={onSubmit}>
              {inner}
            </form>
          ) : (
            inner
          )}
          <button type="button" className={styles.close} aria-label={closeLabel} disabled={!dismissible} onClick={requestClose}>
            <CloseIcon />
          </button>
        </div>
      </PortalRoot>
    </dialog>,
    document.body,
  )
}
